import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Receipt } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PageHeader, SectionCard, EmptyState, TABLE_HEAD, TABLE_ROW } from "../components/common";
import { DepotField, SimpleSelect } from "../components/DepotField";
import { CartEditor, cartItems } from "../components/CartEditor";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { useDepot } from "../context/DepotContext";
import { api, formatApiError, money, timeFmt, todayStr, TZ, PAYMENT_METHODS } from "../lib/api";

function useFormDepot() {
  const { user, isAdmin } = useAuth();
  const { depotParam, depots } = useDepot();
  const [depotId, setDepotId] = useState("");
  useEffect(() => {
    if (!isAdmin) setDepotId(user.depot_id);
    else if (depotParam) setDepotId(depotParam);
    else if (!depotId && depots.length) setDepotId(depots[0].id);
  }, [isAdmin, user, depotParam, depots, depotId]);
  return [depotId, setDepotId];
}

export function CustomerFields({ customer, setCustomer, phone, setPhone, required, prefix }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5"><Label className="label-mono">Customer{required ? " (required)" : ""}</Label>
        <Input value={customer} required={required} onChange={(e) => setCustomer(e.target.value)} placeholder={required ? "Customer name" : "Walk-in customer"} className="bg-slate-950" data-testid={`${prefix}-customer-input`} /></div>
      <div className="space-y-1.5"><Label className="label-mono">Phone</Label>
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Optional" className="bg-slate-950" data-testid={`${prefix}-phone-input`} /></div>
    </div>
  );
}

export { useFormDepot };

export default function Sell() {
  const navigate = useNavigate();
  const { depotParam, scopeLabel } = useDepot();
  const [depotId, setDepotId] = useFormDepot();
  const [lines, setLines] = useState([]);
  const [customer, setCustomer] = useState("");
  const [phone, setPhone] = useState("");
  const [payment, setPayment] = useState("Cash");
  const [busy, setBusy] = useState(false);
  const products = useApi("/products", { depot_id: depotParam });
  const sales = useApi("/sales", { depot_id: depotParam, date: todayStr(), tz: TZ });

  const submit = async (e) => {
    e.preventDefault();
    if (!lines.length) { toast.error("Add at least one product"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sales", { depot_id: depotId, customer, customer_phone: phone, payment_method: payment, items: cartItems(lines) });
      toast.success(`Sale saved · ${data.receipt_no}`);
      navigate(`/sales/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={scopeLabel} title="New Sale" subtitle="Sell several items at once and print a receipt (A4 or 80mm). Credit sales print as an invoice." />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <SectionCard title="Cart" className="xl:col-span-7 self-start">
          <form onSubmit={submit} className="space-y-4 p-5" data-testid="sell-form">
            <DepotField value={depotId} onChange={(v) => { setDepotId(v); setLines([]); }} testid="sell-depot-select" />
            <CartEditor products={products.data || []} depotId={depotId} lines={lines} setLines={setLines} testid="sell-cart" />
            <CustomerFields customer={customer} setCustomer={setCustomer} phone={phone} setPhone={setPhone} required={payment === "Credit"} prefix="sell" />
            <SimpleSelect label="Payment" value={payment} onChange={setPayment} options={PAYMENT_METHODS} testid="sell-payment-select" />
            <Button type="submit" disabled={busy} className="h-11 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="sell-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />} Complete sale &amp; print receipt
            </Button>
          </form>
        </SectionCard>
        <SectionCard title="Today's receipts" className="xl:col-span-5">
          {!sales.data?.length ? <EmptyState text="No receipts today" testid="sales-empty" /> : (
            <table className="w-full text-sm" data-testid="sales-table">
              <thead className={TABLE_HEAD}><tr><th className="px-4 py-2 text-left">Receipt</th><th className="px-4 py-2 text-left">Customer</th><th className="px-4 py-2 text-right">Total</th></tr></thead>
              <tbody>{sales.data.map((s) => (
                <tr key={s.id} className={`${TABLE_ROW} ${s.status === "cancelled" ? "opacity-60" : ""}`}>
                  <td className="px-4 py-2"><Link to={`/sales/${s.id}`} className="font-mono text-amber-400 hover:underline" data-testid={`sale-link-${s.receipt_no}`}>{s.receipt_no}</Link><div className="text-xs text-slate-500">{timeFmt(s.created_at)} · {s.payment_method}{s.status === "cancelled" && <span className="ml-1 font-semibold text-rose-400" data-testid={`sale-cancelled-${s.receipt_no}`}>· CANCELLED</span>}</div></td>
                  <td className="px-4 py-2 text-slate-300">{s.customer || "Walk-in"}</td>
                  <td className={`px-4 py-2 text-right font-mono ${s.status === "cancelled" ? "line-through" : ""}`}>{money(s.total)}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
